import { useEffect, useState } from "react";
import {
  formatBytes,
  DIRECT_UPLOAD_MAX_BYTES,
  FILE_MAX_BYTES,
  PLANS,
  PAYG_BYTES_PER_DOLLAR,
  PAYG_PRICE,
} from "@saucer/core";

const SECTIONS = [
  { id: "start", label: "Getting started" },
  { id: "install", label: "Install the CLI" },
  { id: "auth", label: "Signing in" },
  { id: "upload", label: "Uploading" },
  { id: "links", label: "Links" },
  { id: "images", label: "Image transforms" },
  { id: "privacy", label: "Public & private" },
  { id: "passwords", label: "Passwords" },
  { id: "search", label: "Search & assistant" },
  { id: "stats", label: "Stats" },
  { id: "tokens", label: "Tokens & CI" },
  { id: "agents", label: "Coding agents" },
  { id: "plans", label: "Plans & quotas" },
  { id: "limits", label: "Limits & guarantees" },
] as const;

/** The whole product, one page, with a sidebar that tracks where you are. */
export function Docs() {
  const active = useScrollSpy(SECTIONS.map((s) => s.id));

  return (
    <div className="docs2">
      <aside className="docs2__side">
        <span className="docs2__side-head">Documentation</span>
        <nav aria-label="Sections">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className={active === s.id ? "docs2__side--active" : undefined}
            >
              {s.label}
            </a>
          ))}
        </nav>
      </aside>

      <main className="docs2__main">
        <header className="docs2__head">
          <span className="saas-chip">Docs</span>
          <h1 className="docs2__title">Everything saucer does</h1>
          <p className="docs2__lede">
            saucer turns files into permanent URLs under your name. Ten minutes here covers
            the whole product; after that it&rsquo;s one command and a link.
          </p>
        </header>

        <section id="start" className="docs2__section">
          <h2>Getting started</h2>
          <p>
            Sign in with GitHub and the account exists. There is no waiting list and no code
            to redeem — you land on the free plan, with{" "}
            {formatBytes(PLANS.free.storageBytes)} of storage and the whole product turned
            on. Your GitHub username becomes your namespace, and every file you upload lives
            at a link inside it:
          </p>
          <pre className="codecard mono">saucer.sh/&lt;you&gt;/&lt;filename&gt;</pre>
          <p>
            There are two ways in: drop a file on the dashboard, or use the CLI. Both end the
            same way — with a link you can hand to anyone. Links never expire on their own;
            the only thing that kills a link is you deleting the file behind it.
          </p>
          <p>
            Invite codes still exist, but they no longer gate anything: a code is attribution
            only, a record of who sent you. Enter one on the sign-in page if you have one, or
            skip it. Admins mint them from Settings, or with the node CLI:
          </p>
          <pre className="codecard mono">
            {"npx saucer-sh invite 3 --note teammates   "}<i>prints three codes</i>{"\n"}
            {"npx saucer-sh invites                     "}<i>lists codes and who redeemed them</i>
          </pre>
          <p>
            What you store is what is capped. See <a href="/pricing">the plans</a> for the
            numbers, or <a href="#plans">Plans &amp; quotas</a> below for how the quota
            behaves.
          </p>
        </section>

        <section id="install" className="docs2__section">
          <h2>Install the CLI</h2>
          <p>
            The CLI is a native Zig binary — a ~1 MB static executable for macOS and Linux
            with no runtime to install. One command puts it in{" "}
            <code className="mono">~/.local/bin</code>:
          </p>
          <pre className="codecard mono">curl -fsSL https://saucer.sh/install.sh | sh</pre>
          <p>
            It keeps a warm TLS connection to the API in a background daemon, which is why
            commands answer in about 35 ms instead of paying a handshake every time.
            Upgrade it in place any time:
          </p>
          <pre className="codecard mono">saucer update</pre>
          <p>
            On Windows, or if you prefer npm: <code className="mono">npx saucer-sh</code>{" "}
            speaks the same API and reads the same config file. The two overlap but are not
            identical — the native binary covers the everyday loop, and the node CLI owns the
            commands that need a browser or an account-level action:
          </p>
          <div className="cmdrows">
            {CLI_COMMANDS.map(([command, what]) => (
              <div key={command} className="cmdrows__row">
                <code className="mono">{command}</code>
                <span>{what}</span>
              </div>
            ))}
          </div>
        </section>

        <section id="auth" className="docs2__section">
          <h2>Signing in</h2>
          <p>
            Anyone can sign in with GitHub, and the first sign-in creates the account on the
            free plan. Nothing is gated behind an invite.
          </p>
          <p>
            Machines authenticate with a device flow. The native binary has no{" "}
            <code className="mono">login</code> command — run the node one once and the
            binary reads the token it wrote to{" "}
            <code className="mono">~/.config/saucer/config.json</code>:
          </p>
          <pre className="codecard mono">
            <b>$</b>{" npx saucer-sh login\n"}
            {"  visit  https://saucer.sh/cli?code=y1vf-qfxm\n"}
            {"  signed in as you\n"}
            <b>$</b>{" saucer whoami\n"}
            {"  you \u00b7 42 files"}
          </pre>
          <p>
            The CLI prints a short code and opens your browser; you approve it while signed
            in, and that machine gets its own long-lived token. Revoke any machine from
            Settings and it is signed out immediately.{" "}
            <code className="mono">npx saucer-sh logout</code> forgets the local token.
          </p>
        </section>

        <section id="upload" className="docs2__section">
          <h2>Uploading</h2>
          <p>
            <code className="mono">saucer send</code> takes one or more files and prints one
            link per file. Files up to {formatBytes(DIRECT_UPLOAD_MAX_BYTES)} go up in a
            single request; anything bigger is split into parts and streamed straight to
            storage as a resumable multipart upload, up to {formatBytes(FILE_MAX_BYTES)} —
            a dropped connection resumes instead of starting over.
          </p>
          <div className="cmdrows">
            {UPLOAD_FLAGS.map(([flag, what]) => (
              <div key={flag} className="cmdrows__row">
                <code className="mono">{flag}</code>
                <span>{what}</span>
              </div>
            ))}
          </div>
          <p>Two more ways bytes get in without touching your disk:</p>
          <pre className="codecard mono">
            {"saucer paste                 "}<i>uploads the image on your clipboard</i>{"\n"}
            {"npx saucer-sh snap <url>     "}<i>screenshots a page in a headless browser</i>
          </pre>
          <p>
            After the bytes land, a pipeline runs automatically at the edge: a thumbnail is
            cut, a social card is rendered so the link unfurls in Slack and PRs, and AI
            writes a title, description and tags that feed search. None of it blocks your
            upload — the link works the moment the bytes arrive.
          </p>
        </section>

        <section id="links" className="docs2__section">
          <h2>Links</h2>
          <p>
            A bare link does one of two things, chosen per file and changeable any time
            without breaking the URL: render a <em>preview page</em> with the file in it, or
            hand over the <em>raw bytes</em>. Two suffixes always work regardless of that
            choice:
          </p>
          <pre className="codecard mono">
            {"…/report.pdf/raw        "}<i>the bytes, always</i>{"\n"}
            {"…/report.pdf/download   "}<i>the bytes, as an attachment</i>
          </pre>
          <p>
            The name and the file are separate things. Renaming a link (
            <code className="mono">--as</code> at upload, or from the dashboard) moves the
            name; the file behind it never moves and its history is untouched. The old name
            stops working immediately. Deleting a file kills its link immediately too —
            storage cleanup happens in the background, but the URL dies with the delete.
          </p>
        </section>

        <section id="images" className="docs2__section">
          <h2>Image transforms</h2>
          <p>
            Every image link doubles as an on-the-fly transform endpoint. Add parameters to
            the <code className="mono">/img</code> suffix and the edge resizes and re-encodes
            on the way out — the original is never modified:
          </p>
          <pre className="codecard mono">
            {"…/photo.jpg/img?w=800            "}<i>resize to 800px wide</i>{"\n"}
            {"…/photo.jpg/img?w=800&fmt=webp   "}<i>and re-encode as webp</i>{"\n"}
            {"…/photo.jpg/img?w=400&fit=cover  "}<i>crop to fill instead of fit</i>
          </pre>
          <p>
            Transforms are cached at the edge, so hot sizes cost nothing after the first
            request. Use them anywhere an <code className="mono">&lt;img&gt;</code> tag
            lives — the link is the API.
          </p>
        </section>

        <section id="privacy" className="docs2__section">
          <h2>Public &amp; private files</h2>
          <p>
            Every file is public or private. A public link opens for anyone who has it —
            that is the default, and what a file-sharing link is usually for. A private link
            opens only while you are signed in; everyone else gets the same 404 as a link
            that never existed, so a private link does not even confirm there is a file
            behind it.
          </p>
          <pre className="codecard mono">
            {"saucer send notes.md --private   "}<i>this upload, private</i>{"\n"}
            {"saucer send demo.mp4 --pub       "}<i>this upload, public</i>{"\n"}
            {"saucer privacy private           "}<i>every upload from now on, private</i>{"\n"}
            {"saucer privacy                   "}<i>show the current default</i>
          </pre>
          <p>
            The flags win over the default: with{" "}
            <code className="mono">saucer privacy private</code> set,{" "}
            <code className="mono">--pub</code> still publishes that one file. Flip a single
            file later from the dashboard — each file card has a public/private toggle — or
            through the API (<code className="mono">PATCH /api/files/:id</code>); the link
            itself never changes. Scripts open their own private files by sending the CLI
            token:
          </p>
          <pre className="codecard mono">
            {'curl -H "authorization: Bearer $SAUCER_TOKEN" https://saucer.sh/you/notes.md/raw'}
          </pre>
          <p>
            Private is about who can open the link; a <a href="#passwords">password</a> is
            for sharing with someone specific. They combine: a private, passworded file asks
            you to sign in <em>and</em> unlock.
          </p>
        </section>

        <section id="passwords" className="docs2__section">
          <h2>Password-protected links</h2>
          <p>
            Without a password, anyone who has the link can open it — that is the point of a
            link. When that is not the point, lock it:
          </p>
          <pre className="codecard mono">saucer send contract.pdf --password 203932</pre>
          <p>
            A locked link shows a password gate; nothing about the file — not the name, not
            the type — is revealed until the password is entered. Browsers remember the
            unlock for an hour. Scripts send it as a header, which keeps it out of URLs,
            access logs and shell history:
          </p>
          <pre className="codecard mono">
            {'curl -H "x-saucer-pw: 203932" https://saucer.sh/you/contract.pdf/raw'}
          </pre>
          <p>
            Passwords are stored only as hashes and compared in constant time. There is no
            recovery — if the password is lost, delete the file and upload it again.
          </p>
        </section>

        <section id="search" className="docs2__section">
          <h2>Search &amp; assistant</h2>
          <p>
            Because every upload is described and indexed, you search by what a file{" "}
            <em>is</em>, not what it happens to be called:
          </p>
          <pre className="codecard mono">
            <b>$</b>{" saucer search that chart about billing\n"}
            {"  revenue-q3.png        billing dashboard, bar chart\n"}
            {"  invoice-flow.pdf      payment sequence diagram"}
          </pre>
          <p>
            The assistant goes one step further: it is an agent with tools over your own
            files — ask it to find, rename, flip a link to raw, or clean things up, in the
            dashboard or with <code className="mono">npx saucer-sh ask</code>. It only ever
            sees your account.
          </p>
        </section>

        <section id="stats" className="docs2__section">
          <h2>Stats</h2>
          <p>
            <a href="/stats">/stats</a> is one page about how your links are actually used:
            account totals, a {PLANS.free.statsDays}-day activity chart, your most-opened
            files, the countries, referrers and platforms opening them, a breakdown by event
            kind, hour-of-day and day-of-week rhythm, what your library is made of, upload
            size percentiles, and a feed of recent events.
          </p>
          <p>
            The numbers come from the R2 Data Catalog — the event log, stored as Apache
            Iceberg — which a cron rolls up every 15 minutes into a single blob. Loading the
            page is then one KV read, so it answers in milliseconds rather than running
            queries while you wait. The trade is freshness: figures are up to fifteen minutes
            behind, and a brand new account sees nothing until the first rollup runs.
          </p>
          <p>
            Free keeps {PLANS.free.statsDays} days of history; Pro keeps all of it. See{" "}
            <a href="#plans">Plans &amp; quotas</a>.
          </p>
        </section>

        <section id="tokens" className="docs2__section">
          <h2>Tokens &amp; CI</h2>
          <p>
            <code className="mono">npx saucer-sh login</code> covers machines you sit at.
            For everything else — CI, cron, servers — mint a token in Settings and pass it
            through the environment. Every command accepts it and never needs a browser:
          </p>
          <pre className="codecard mono">SAUCER_TOKEN=&lt;token&gt; saucer send dist/app.zip --quiet</pre>
          <p>
            <code className="mono">--quiet</code> prints links only, so the output is safe to
            capture into variables or logs. Tokens are stored server-side as hashes, can be
            revoked individually, and revocation takes effect immediately.
          </p>
          <p>
            That combination makes moving a file between two machines one command on either
            side — no SSH keys exchanged, no ports opened, works through any firewall:
          </p>
          <pre className="codecard mono">
            {"server$  saucer send /var/log/app/crash.log --quiet\n"}
            {"laptop$  curl -fsSL https://saucer.sh/you/crash.log/raw -o crash.log"}
          </pre>
          <p>
            <code className="mono">SAUCER_HOST</code> points the CLI at a different
            deployment, if you run your own.
          </p>
        </section>

        <section id="agents" className="docs2__section">
          <h2>Teach your coding agent</h2>
          <p>
            saucer ships a skill — a markdown playbook that teaches a coding agent the whole
            workflow: installing the CLI, authenticating headlessly, sharing files with you,
            moving files between machines, and fetching them back with curl. For Claude
            Code, install it once and it loads automatically whenever file sharing comes up:
          </p>
          <pre className="codecard mono">
            {"mkdir -p ~/.claude/skills/saucer &&\\\n"}
            {"curl -fsSL https://saucer.sh/skills/saucer/SKILL.md \\\n"}
            {"  -o ~/.claude/skills/saucer/SKILL.md"}
          </pre>
          <p>
            Any agent that reads <code className="mono">AGENTS.md</code> learns it the same
            way:
          </p>
          <pre className="codecard mono">
            {"curl -fsSL https://saucer.sh/skills/saucer/SKILL.md >> AGENTS.md"}
          </pre>
          <p>
            Then give the agent a token — set <code className="mono">SAUCER_TOKEN</code> in
            its environment. The skill covers the commands, the raw-bytes URLs, error
            recovery, and the safety rules (what not to upload, how to keep passwords out of
            logs), so the agent behaves like someone who has read these docs.
          </p>
        </section>

        <section id="plans" className="docs2__section">
          <h2>Plans &amp; quotas</h2>
          <p>
            Free is the whole product with a smaller cupboard:{" "}
            {formatBytes(PLANS.free.storageBytes)} of storage, files up to{" "}
            {formatBytes(PLANS.free.maxFileBytes)}, and {PLANS.free.statsDays} days of stats
            history. Pro is ${PLANS.pro.price} a month for{" "}
            {formatBytes(PLANS.pro.storageBytes)}, files up to{" "}
            {formatBytes(PLANS.pro.maxFileBytes)}, unlimited stats history and priority in
            the conversion sandbox. Nothing else differs — search, the assistant, private
            links, passwords and transforms are on either way.
          </p>
          <p>
            Bandwidth is unlimited on both, and that is not a fair-use promise with a
            trapdoor: the storage this runs on does not charge for egress, so a link that
            goes wide costs the same as one nobody opens. Only what you keep is metered.
          </p>
          <div className="cmdrows">
            {PLAN_ROWS.map(([label, value]) => (
              <div key={label} className="cmdrows__row">
                <code className="mono">{label}</code>
                <span>{value}</span>
              </div>
            ))}
          </div>
          <p>
            If you want a little more room rather than a whole plan, take it by the
            slice:{" "}
            {formatBytes(PAYG_BYTES_PER_DOLLAR)} for ${PAYG_PRICE} a month, recurring, and it
            stacks on top of Free or Pro as many times as you like.
          </p>
          <p>
            Payment is manual. There is no payment provider wired up: you request an upgrade
            from <a href="/pricing">the pricing page</a>, settle it directly, and an admin
            moves the account. It is a small service run by one person, and pretending
            otherwise would only add a middleman.
          </p>
          <p>
            Going over quota never breaks anything you have already shared. Every existing
            link keeps serving, forever, at full speed. What stops is new uploads: the API
            refuses them with <code className="mono">413</code> and the error code{" "}
            <code className="mono">quota_exceeded</code>, before any bytes move. Usage is read
            live rather than carried as a running total, so deleting a file frees its space
            immediately and the next upload goes through.
          </p>
        </section>

        <section id="limits" className="docs2__section">
          <h2>Limits &amp; guarantees</h2>
          <div className="cmdrows">
            {LIMITS.map(([label, value]) => (
              <div key={label} className="cmdrows__row">
                <code className="mono">{label}</code>
                <span>{value}</span>
              </div>
            ))}
          </div>
          <p>
            Served bytes carry a strict <code className="mono">content-security-policy</code>{" "}
            and can never execute as a page on the saucer origin — an uploaded HTML file
            downloads, it does not run. Sessions, tokens and file passwords are stored as
            hashes only.
          </p>
        </section>
      </main>
    </div>
  );
}

/** Highlights the sidebar link for the section currently on screen. */
function useScrollSpy(ids: readonly string[]): string {
  const [active, setActive] = useState<string>(ids[0] ?? "");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActive(entry.target.id);
        }
      },
      { rootMargin: "-20% 0px -70% 0px" },
    );
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [ids]);

  return active;
}

const UPLOAD_FLAGS: [string, string][] = [
  ["--as <name>", "Claim a specific link name instead of the filename"],
  ["--rename <name>", "Upload under a different filename (npx saucer-sh only)"],
  ["--raw", "Make the bare link hand over bytes instead of a page"],
  ["--pub / --private", "Who the link opens for: anyone, or only you"],
  ["--password <pass>", "Lock the link behind a password"],
  ["--quiet", "Print links only — for scripts and CI"],
  ["--png", "Keep a pasted clipboard image lossless (native CLI, paste only)"],
];

const CLI_COMMANDS: [string, string][] = [
  ["send <file> (alias up)", "Upload files and print their links"],
  ["paste", "Upload the image on your clipboard"],
  ["ls", "List your files, newest first"],
  ["rm <slug>", "Delete a file and break its link"],
  ["search <words>", "Find your files by what is in them"],
  ["privacy [public|private]", "Show or set what new uploads become"],
  ["whoami", "Show who this machine is signed in as"],
  ["update", "Replace the binary with the latest build"],
  ["login / logout", "Device flow, and forgetting the token — npx saucer-sh only"],
  ["snap <url>", "Screenshot a web page and keep the link — npx saucer-sh only"],
  ["ask <question>", "Ask the assistant about your files — npx saucer-sh only"],
  ["invite [count] / invites", "Mint and list invite codes, admin — npx saucer-sh only"],
];

const PLAN_ROWS: [string, string][] = [
  [
    "Free",
    `${formatBytes(PLANS.free.storageBytes)} storage, ${formatBytes(
      PLANS.free.maxFileBytes,
    )} per file, ${PLANS.free.statsDays} days of stats — $${PLANS.free.price}`,
  ],
  [
    "Pro",
    `${formatBytes(PLANS.pro.storageBytes)} storage, ${formatBytes(
      PLANS.pro.maxFileBytes,
    )} per file, unlimited stats — $${PLANS.pro.price} a month`,
  ],
  [
    "Extra storage",
    `${formatBytes(PAYG_BYTES_PER_DOLLAR)} for $${PAYG_PRICE} a month, on either plan`,
  ],
  ["Bandwidth", "Unlimited on both — egress is never charged or throttled"],
  ["Payment", "Manual: request an upgrade, an admin applies it"],
];

const LIMITS: [string, string][] = [
  [
    "Storage",
    `${formatBytes(PLANS.free.storageBytes)} on Free, ${formatBytes(
      PLANS.pro.storageBytes,
    )} on Pro, plus any pay-as-you-go on top`,
  ],
  [
    "Largest single file",
    `${formatBytes(PLANS.free.maxFileBytes)} on Free, ${formatBytes(
      PLANS.pro.maxFileBytes,
    )} on Pro — the platform ceiling is ${formatBytes(FILE_MAX_BYTES)}`,
  ],
  ["Bandwidth", "Unlimited on both plans"],
  [
    "Single-request uploads",
    `up to ${formatBytes(DIRECT_UPLOAD_MAX_BYTES)}, then multipart takes over`,
  ],
  ["Stats history", `${PLANS.free.statsDays} days on Free, unlimited on Pro`],
  ["Over quota", "New uploads refused with 413 quota_exceeded; every existing link keeps serving"],
  ["Link lifetime", "Forever — only deleting the file kills the link"],
  ["Link rename", "Instant, storage untouched"],
  ["Delete", "Link dies immediately, the space is free immediately, storage swept in the background"],
];
